import { useQuery } from "react-query";
import {
  getSalesforceSyncSettings,
  SalesforceSyncRequestError,
  SalesforceSyncSettings,
} from "store/persistence/persistSalesforceSync";
import type { SalesforceSyncSettingsView } from "store/persistence/persistSalesforceMapping";
import { salesforceSyncQueryKeys } from "store/react-query/queryKeys";
import { useStores } from "store/useStore";
import { planIncludesSalesforce } from "./salesforceV2Tools";

/**
 * How long the sync mode is kept before the flow editor reads it again. The mode
 * changes rarely (staff move an account between the old sync, shadow and v2),
 * and the server refuses the Salesforce v2 triggers and steps to an account that
 * is not on v2 anyway, so the editor asks once when it opens and then no more
 * often than this.
 */
export const SYNC_MODE_STALE_MS = 10 * 60 * 1000;

/** A refusal for a missing permission (403) does not change by itself. */
const isNoPermission = (error: unknown) =>
  (error as SalesforceSyncRequestError | null | undefined)?.status === 403;

/**
 * The account's Salesforce sync settings, read for the toolbox. It shares the
 * Data Sync page's cache entry (so a mode already read there is not read again),
 * but it does not poll: the Data Sync page asks every minute because a customer
 * can have that page open while staff change the mode, and the flow editor, open
 * for hours, must not ask the server every minute for an answer that decides
 * only which buttons are listed.
 */
const useSyncModeQuery = (enabled: boolean) =>
  useQuery<SalesforceSyncSettings, SalesforceSyncRequestError>(
    salesforceSyncQueryKeys.settings(),
    getSalesforceSyncSettings,
    {
      enabled,
      staleTime: SYNC_MODE_STALE_MS,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) =>
        !isNoPermission(error) && failureCount < 2,
    }
  );

/**
 * Whether this account may be offered the Salesforce v2 triggers and steps:
 * Salesforce connected, a plan that includes it, and the new sync switched on.
 * The sync mode is the settings route's answer, so until it has come (or when it
 * cannot be read, for example for a member without the Apps permission) the
 * answer is no: a trigger or step is never offered on a guess.
 *
 * Call it from an observer component, since it reads the account store.
 */
export const useSalesforceV2Access = (): boolean => {
  const { accountStore } = useStores();
  const account = accountStore.account;
  const connected = Boolean(account?.services?.salesforce);
  const planOk = planIncludesSalesforce({
    tier: accountStore.accountTier,
    canUseSalesforce: account?.payment?.appsumo?.canUseSalesforce,
  });
  const settings = useSyncModeQuery(connected && planOk);
  return connected && planOk && settings.data?.mode === "v2";
};

/**
 * Whether the org has Person Accounts, as far as the last Refresh fields saw (the
 * settings answer's `personAccounts`), for an account that may use the Salesforce
 * v2 triggers and steps. It is the same call and cache entry as the access above,
 * read without polling, so a card on the canvas adds no request of its own. False
 * for every other account, and until the answer has come: a card never talks of
 * Person Accounts on a guess.
 *
 * Call it from an observer component, since it reads the account store.
 */
export const useSalesforcePersonAccounts = (): boolean => {
  const allowed = useSalesforceV2Access();
  const settings = useSyncModeQuery(allowed);
  return (
    allowed &&
    (settings.data as SalesforceSyncSettingsView | undefined)
      ?.personAccounts === true
  );
};
